class DiscountWheelComponent extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.dialog = this.querySelector('dialog-component');
    this.trigger = this.querySelector('[data-wheel-trigger]');
    this.disc = this.querySelector('[data-wheel-disc]');
    this.spinBtn = this.querySelector('[data-wheel-spin]');
    this.resultEl = this.querySelector('[data-wheel-result]');
    this.resultMessageEl = this.querySelector('[data-wheel-result-message]');
    this.codeRowEl = this.querySelector('[data-wheel-code-row]');
    this.codeEl = this.querySelector('[data-wheel-code]');
    this.copyBtn = this.querySelector('[data-wheel-copy]');
    this.useNowEl = this.querySelector('[data-wheel-use-now]');
    this.leadForm = this.querySelector('[data-wheel-lead-form]');
    this.leadFormEl = this.querySelector('#WheelLeadForm');
    this.leadNameInput = this.querySelector('[data-wheel-lead-name]');
    this.leadFirstNameInput = this.querySelector('[data-wheel-lead-first-name]');
    this.leadLastNameInput = this.querySelector('[data-wheel-lead-last-name]');
    this.leadWhatsappInput = this.querySelector('[data-wheel-lead-whatsapp]');
    this.leadEmailInput = this.querySelector('[data-wheel-lead-email]');
    this.leadErrorEl = this.querySelector('[data-wheel-lead-error]');

    this.requireContact = this.dataset.requireContact === 'true';

    const configEl = this.querySelector('[data-wheel-config]');
    try {
      this.config = JSON.parse(configEl?.textContent || '{}');
    } catch (e) {
      this.config = { segments: [] };
    }
    this.segments = (this.config.segments || []).filter((s) => s.label);

    this.spun = false;
    this.pendingSegment = null;
    this.leadCaptured = false;
    this.currentRotation = 0;
    this.positionLabels();

    this.trigger?.addEventListener('click', () => this.open());
    this.spinBtn?.addEventListener('click', () => this.spin());
    this.copyBtn?.addEventListener('click', () => this.copyCode());
    this.leadFormEl?.addEventListener('submit', (event) => this.handleLeadSubmit(event));
    // Máscara brasileira ((DD) 9XXXX-XXXX) enquanto a pessoa digita — só aceita
    // número, reduzindo o risco de erro de digitação no WhatsApp.
    this.leadWhatsappInput?.addEventListener('input', () => this.formatPhoneInput());

    this.maybeAutoOpen();
  }

  storageKey() {
    return this.dataset.storageKey || 'discount_wheel_shown';
  }

  markShown() {
    localStorage.setItem(this.storageKey(), String(Date.now()));
  }

  maybeAutoOpen() {
    // "auto_home": abre sozinha a cada visita à página inicial (sem checar
    // se já foi mostrada antes — é intencional, o lojista pediu "toda vez").
    // "button": não abre sozinha, só quando o cliente clica no botão flutuante.
    if (this.dataset.displayMode !== 'auto_home') return;

    const delay = parseFloat(this.dataset.autoHomeDelay || '1');
    setTimeout(() => this.open(), Math.max(0, delay) * 1000);
  }

  open() {
    this.dialog?.showDialog?.();
    this.markShown();
  }

  positionLabels() {
    const n = this.segments.length;
    if (!n) return;
    const step = 360 / n;
    // Cada fatia tem um wrapper do tamanho inteiro do disco (position: absolute; inset: 0),
    // então o centro de rotação padrão (50% 50%) já cai exatamente no centro do disco.
    // Rotacionar o wrapper (em vez do <span> de texto direto) evita que o texto fique
    // desalinhado/cortado, já que o texto só cuida do próprio posicionamento dentro dele.
    const wrapEls = this.querySelectorAll('.discount-wheel__seg-label-wrap');
    wrapEls.forEach((el, i) => {
      const angle = step * i + step / 2;
      el.style.transform = `rotate(${angle}deg)`;
    });
    if (this.disc) {
      const stops = this.segments
        .map((seg, i) => {
          const start = (step * i * 100) / 360;
          const end = (step * (i + 1) * 100) / 360;
          return `${seg.color} ${start}% ${end}%`;
        })
        .join(', ');
      this.disc.style.background = `conic-gradient(${stops})`;
    }
  }

  pickSegment() {
    // Fatias marcadas como "nunca sortear" ficam visíveis na roda (compõem o
    // visual/promessa), mas são excluídas do sorteio de verdade — o ponteiro
    // nunca para nelas.
    const eligible = this.segments
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => !s.neverWin);
    const pool = eligible.length ? eligible : this.segments.map((s, i) => ({ s, i }));

    const totalWeight = pool.reduce((sum, { s }) => sum + (s.weight || 1), 0);
    let roll = Math.random() * totalWeight;
    for (const { s, i } of pool) {
      roll -= s.weight || 1;
      if (roll <= 0) return i;
    }
    return pool[pool.length - 1].i;
  }

  spin() {
    if (this.spun || !this.segments.length) return;
    this.spun = true;
    this.spinBtn.disabled = true;
    if (this.resultEl) this.resultEl.hidden = true;

    const index = this.pickSegment();
    const n = this.segments.length;
    const step = 360 / n;
    const segmentCenter = step * index + step / 2;
    const fullSpins = 5 * 360;
    // A roda gira, o ponteiro fica fixo no topo (0deg); precisamos que o
    // centro do segmento sorteado pare embaixo do ponteiro. A rotação é
    // sempre somada à posição atual (nunca reiniciada pra 0), pra girar pra
    // frente mesmo em um segundo/terceiro giro, sem "voltar" visualmente.
    const currentAngle = ((this.currentRotation % 360) + 360) % 360;
    const targetAngle = (360 - segmentCenter + 360) % 360;
    const delta = fullSpins + ((targetAngle - currentAngle + 360) % 360);
    this.currentRotation += delta;

    this.disc.style.transform = `rotate(${this.currentRotation}deg)`;

    setTimeout(() => this.afterSpin(this.segments[index]), 4600);
  }

  afterSpin(segment) {
    this.spinBtn.hidden = true;

    if (this.requireContact && this.leadForm && !this.leadCaptured) {
      // Só mostra o resultado (mensagem/cupom) depois que a pessoa preencher
      // WhatsApp + e-mail e enviar o formulário, na primeira vez. Se ela já
      // preencheu antes (ex: caiu em "tente de novo" e girou de novo), não
      // pedimos os dados de novo.
      this.pendingSegment = segment;
      this.leadForm.hidden = false;
      return;
    }

    this.showResult(segment);
  }

  // Formata o WhatsApp como (DD) 9XXXX-XXXX enquanto a pessoa digita, aceitando
  // só números — reduz o risco de dado errado por causa de espaço/traço/parêntese
  // digitado do jeito errado.
  formatPhoneInput() {
    if (!this.leadWhatsappInput) return;
    const digits = this.leadWhatsappInput.value.replace(/\D/g, '').slice(0, 11);
    const ddd = digits.slice(0, 2);
    const rest = digits.slice(2);
    let formatted = '';
    if (digits.length > 0) formatted += `(${ddd}`;
    if (digits.length >= 2) formatted += ') ';
    if (rest.length > 5) {
      formatted += `${rest.slice(0, 5)}-${rest.slice(5)}`;
    } else if (rest.length > 4) {
      formatted += `${rest.slice(0, 4)}-${rest.slice(4)}`;
    } else {
      formatted += rest;
    }
    this.leadWhatsappInput.value = formatted;
  }

  // Separa "Nome completo" em first_name/last_name pros campos ocultos que a
  // Shopify espera — a pessoa só vê um campo único, mais simples de preencher.
  splitFullName(fullName) {
    const parts = fullName.trim().split(/\s+/);
    const first = parts.shift() || '';
    const last = parts.join(' ');
    return { first, last };
  }

  handleLeadSubmit(event) {
    if (!this.pendingSegment) {
      event.preventDefault();
      return;
    }

    const name = (this.leadNameInput?.value || '').trim();
    const phoneDigits = (this.leadWhatsappInput?.value || '').replace(/\D/g, '');
    const email = (this.leadEmailInput?.value || '').trim();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const phoneValid = phoneDigits.length === 10 || phoneDigits.length === 11;

    let errorMessage = '';
    if (!name) {
      errorMessage = 'Digite seu nome pra continuar.';
    } else if (!phoneValid) {
      errorMessage = 'Confira o WhatsApp (com DDD) antes de continuar.';
    } else if (!emailValid) {
      errorMessage = 'Digite um e-mail válido pra continuar.';
    }

    if (errorMessage) {
      event.preventDefault();
      if (this.leadErrorEl) {
        this.leadErrorEl.hidden = false;
        this.leadErrorEl.textContent = errorMessage;
      }
      return;
    }

    // Preenche os campos ocultos (nome dividido, telefone em E.164 com +55)
    // logo antes do envio real — assim o formulário visível fica simples pro
    // cliente preencher, mas o dado chega estruturado certo no Admin.
    const { first, last } = this.splitFullName(name);
    if (this.leadFirstNameInput) this.leadFirstNameInput.value = first;
    if (this.leadLastNameInput) this.leadLastNameInput.value = last;
    if (this.leadWhatsappInput) this.leadWhatsappInput.value = `+55${phoneDigits}`;

    // Válido: NÃO chamamos preventDefault aqui — deixamos o <form> nativo
    // ({% form 'customer' %}, visível na tela) submeter de verdade pro iframe
    // oculto. É essa submissão "de verdade" (clique real, formulário visível)
    // que dá pro script de proteção contra spam da Shopify anexar o token de
    // CAPTCHA; um fetch cru ou um form.submit() via JS não passam por isso.
    // O envio do cupom por WhatsApp em si (mensagem automática) depende de uma
    // integração à parte (ex: Z-API) que ainda não está conectada — isso aqui só
    // guarda o contato como Cliente da loja.
    if (this.leadErrorEl) this.leadErrorEl.hidden = true;
    this.leadCaptured = true;

    if (this.leadForm) this.leadForm.hidden = true;
    this.showResult(this.pendingSegment);
    this.pendingSegment = null;
  }

  showResult(segment) {
    this.resultEl.hidden = false;

    if (segment.isPrize && segment.codes && segment.codes.length) {
      const code = segment.codes[Math.floor(Math.random() * segment.codes.length)];
      this.resultMessageEl.textContent = `Parabéns! Você ganhou: ${segment.label}`;
      this.codeEl.textContent = code;
      this.codeRowEl.hidden = false;
      this.useNowEl.hidden = false;
      this.useNowEl.href = `/discount/${encodeURIComponent(code)}`;
      this.burstConfetti(this.resultEl);
      // Prêmio de verdade: trava a roleta, não dá pra girar de novo na mesma visita.
    } else {
      this.resultMessageEl.textContent = 'Não foi dessa vez! Quer tentar de novo?';
      this.codeRowEl.hidden = true;
      this.useNowEl.hidden = true;
      // Sem prêmio: libera girar de novo (sem pedir os dados de novo).
      this.spun = false;
      this.spinBtn.hidden = false;
      this.spinBtn.disabled = false;
    }
  }

  burstConfetti(container) {
    // Confete leve em CSS puro (sem canvas/libs): reforça a sensação de
    // recompensa quando a pessoa realmente ganha um prêmio na roleta.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const colors = ['#111111', '#ffb703', '#ef476f', '#06d6a0', '#118ab2'];
    for (let i = 0; i < 16; i += 1) {
      const piece = document.createElement('span');
      piece.className = 'discount-wheel__confetti-piece';
      const angle = Math.random() * Math.PI * 2;
      const distance = 70 + Math.random() * 70;
      piece.style.setProperty('--confetti-x', `${Math.cos(angle) * distance}px`);
      piece.style.setProperty('--confetti-y', `${Math.sin(angle) * distance - 50}px`);
      piece.style.setProperty('--confetti-rot', `${Math.round(Math.random() * 360)}deg`);
      piece.style.background = colors[i % colors.length];
      piece.style.animationDelay = `${Math.random() * 0.1}s`;
      container.appendChild(piece);
      piece.addEventListener('animationend', () => piece.remove());
    }
  }

  copyCode() {
    const code = this.codeEl?.textContent;
    if (!code) return;
    navigator.clipboard?.writeText(code).then(() => {
      const original = this.copyBtn.textContent;
      this.copyBtn.textContent = 'Copiado!';
      setTimeout(() => {
        this.copyBtn.textContent = original;
      }, 1500);
    });
  }
}

if (!customElements.get('discount-wheel-component')) {
  customElements.define('discount-wheel-component', DiscountWheelComponent);
}
